import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "./useTranslation";

const Faq = () => {
  const { t } = useTranslation(); 
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleItem = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const faqData = [
    {
      question: t('faq.whatIsEsim.question'),
      answer: t('faq.whatIsEsim.answer'),
      hasLearnMore: true,
      buttonColor: "bg-gray-800"
    },
    {
      question: t('faq.howToSetup.question'),
      answer: t('faq.howToSetup.answer'),
      hasLearnMore: true,
      buttonColor: "bg-purple-500"
    },
    {
      question: t('faq.whatIsAirvoy.question'),
      answer: t('faq.whatIsAirvoy.answer')
    },
    {
      question: t('faq.howSaveRoaming.question'),
      answer: t('faq.howSaveRoaming.answer')
    },
    {
      question: t('faq.needNewSim.question'),
      answer: t('faq.needNewSim.answer')
    },
    {
      question: t('faq.constantConnectivity.question'),
      answer: t('faq.constantConnectivity.answer')
    },
    {
      question: t('faq.freeCallsTexts.question'),
      answer: t('faq.freeCallsTexts.answer')
    },
    {
      question: t('faq.whyChooseAirvoy.question'),
      answer: t('faq.whyChooseAirvoy.answer')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-purple-500 text-sm font-semibold uppercase tracking-wide mb-4">
            {t('faq.headerLabel')}
          </p>
          <h1 className="text-4xl font-semibold text-gray-900 mb-6">
            {t('faq.title')}
          </h1>
        </div>
      </div>

      {/* Featured FAQ Cards */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* What is an eSIM Card */}
            <div className="bg-gray-100 rounded-2xl p-8">
              <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center mb-6">
                <div className="text-gray-600 text-xl">📶</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('faq.whatIsEsim.question')}</h3>
              <p className="text-gray-600 mb-6">
                {t('faq.whatIsEsim.answer')}
              </p>
            </div>

            {/* How to setup your eSIM */}
            <div className="bg-gray-100 rounded-2xl p-8">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <div className="text-purple-600 text-xl">💾</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('faq.howToSetup.question')}</h3>
              <p className="text-gray-600 mb-6">
                {t('faq.howToSetup.answer')}
              </p>
              <Link to="/installation" className="bg-purple-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-purple-600 transition-colors inline-flex items-center gap-2">
                {t('faq.learnMore')} →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Accordion */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-4">
            {faqData.slice(2).map((item, index) => {
              const actualIndex = index + 2; // Adjust index since we're slicing from index 2
              const isExpanded = expandedItems.has(actualIndex);
              
              return (
                <div key={actualIndex} className="border-b border-gray-200">
                  <button
                    onClick={() => toggleItem(actualIndex)}
                    className="w-full py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-8">
                      {item.question}
                    </h3>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <Minus className="w-6 h-6 text-purple-500" />
                      ) : (
                        <Plus className="w-6 h-6 text-purple-500" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="pb-6 pr-14">
                      <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                        {item.answer.split('\n\n').map((paragraph, pIndex) => {
                          // Check if paragraph contains bold text patterns
                          const parts = paragraph.split(/(\*\*.*?\*\*|roaming charges|mobile data|eSIM technology|Airvoy eSIM|eSIM|pay-as-you-go model|eSIM providers|data provider|roaming services)/);
                          
                          return (
                            <p key={pIndex} className={pIndex > 0 ? "mt-4" : ""}>
                              {parts.map((part, partIndex) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return (
                                    <strong key={partIndex} className="font-semibold">
                                      {part.slice(2, -2)}
                                    </strong>
                                  );
                                } else if (['roaming charges', 'mobile data', 'eSIM technology', 'Airvoy eSIM', 'eSIM', 'pay-as-you-go model', 'eSIM providers', 'data provider', 'roaming services'].includes(part)) {
                                  return (
                                    <strong key={partIndex} className="font-semibold">
                                      {part}
                                    </strong>
                                  );
                                }
                                return part;
                              })}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;